/**
 * BIE Institutional Onboarding Workbench models.
 *
 * These contracts match the Pack 5 middleware endpoints:
 *
 * GET  /api/bie/onboarding/load-sessions/:id/workbench
 * GET  /api/bie/onboarding/load-sessions/:id/validation-summary
 * POST /api/bie/onboarding/load-sessions/:id/validate
 * GET  /api/bie/onboarding/validation/issues
 * GET  /api/bie/onboarding/validation/rules
 */

export type BieRecordId = number | string;

export type BieFlag = 0 | 1 | number;

export type BieLoadSessionStateCode =
  | 'DRAFT'
  | 'STAGED'
  | 'VALIDATED'
  | 'REJECTED'
  | 'PUBLISHED'
  | 'CANCELLED'
  | 'ROLLEDBACK'
  | string;

export type BieValidationStateCode =
  | 'PENDING'
  | 'VALID'
  | 'WARNING'
  | 'FAILED'
  | string;

export type BieValidationSeverityCode =
  | 'INFO'
  | 'WARNING'
  | 'ERROR'
  | string;

export type BieOnboardingImportClassCode =
  | 'PRODUCT'
  | 'LOCATION'
  | 'HISTORICAL'
  | 'RECONCILIATION'
  | string;

export type BieOnboardingValidationStatus =
  | 'EMPTY'
  | 'PENDING'
  | 'VALID'
  | 'WARNING'
  | 'FAILED'
  | string;


/* ============================================================
 * Standard API envelopes
 * ============================================================
 */

export interface IBieApiResponse<T> {
  success: boolean;
  data: T;

  message?: string;
}

export interface IBieApiError {
  success: false;

  error?: {
    message?: string;
    code?: string;
    details?: unknown;
  };

  message?: string;
}


/* ============================================================
 * Generic lookup
 * ============================================================
 */

export interface IBieOnboardingLookup {
  rid: BieRecordId;
  rix: string;

  code: string;
  name: string;
}

export interface IBieValidationStateLookup
  extends IBieOnboardingLookup {
  sortOrder: number;
}

export interface IBieValidationSeverityLookup
  extends IBieOnboardingLookup {
  severityLevel: number;
  isBlocking: BieFlag;
}

export interface IBieValidationRuleLookup
  extends IBieOnboardingLookup {
  description?: string | null;

  executionOrder: number;

  severityId: BieRecordId;
  severityCode: BieValidationSeverityCode;
  severityName: string;

  isBlocking: BieFlag;
}


/* ============================================================
 * Workbench Load Session
 * ============================================================
 */

export interface IBieOnboardingWorkbenchLoadSession {
  rid: BieRecordId;
  rix: string;

  code: string;
  name: string;

  description?: string | null;

  stateId: BieRecordId;
  stateCode: BieLoadSessionStateCode;
  stateName: string;

  scopeId: BieRecordId;
  scopeCode: string;
  scopeName: string;

  sourceOrganization: string;
  sourceSystem: string;

  coverageStartDate: string | null;
  coverageEndDate: string | null;
  historicalCutoffDate: string | null;

  observedCoverageStartDate: string | null;
  observedCoverageEndDate: string | null;

  validatorId?: BieRecordId | null;
  validatedAt?: string | null;

  status: number;
  activeStatus: number;

  stamp: string;
}


/* ============================================================
 * Workbench action permissions
 * ============================================================
 */

export interface IBieOnboardingWorkbenchActions {
  canUpload: boolean;
  canStage: boolean;
  canValidate: boolean;
  canRevalidate: boolean;
  canClear: boolean;
  canPublish: boolean;
}


/* ============================================================
 * Import counters
 * ============================================================
 */

// export interface IBieOnboardingImportCounts {
//   total: number;
// }

 export interface IBieOnboardingImportCounts {
  total: number;
  pending: number;
  valid: number;
  warning: number;
  failed: number;
}

export interface IBieOnboardingImportIssueCounts {
  total: number;
  information: number;
  warning: number;
  error: number;
  blocking: number;
}

export interface IBieOnboardingImportResolution {
  resolvedRecordClasses: number;
  unresolvedRecordClasses: number;
  unresolvedReferences: number;
}


/* ============================================================
 * Workbench Import card
 * ============================================================
 */

export interface IBieOnboardingWorkbenchImport {
  rid: BieRecordId;
  rix: string;

  code: string;
  name: string;

  classId: BieRecordId;
  classCode: BieOnboardingImportClassCode;
  className: string;

  sheetName: string;
  filename: string;

  workbookVersion?: string | null;
  checksum?: string | null;

  uploadedBy: BieRecordId;

  uploadedByCode?: string | null;
  uploadedByName?: string | null;

  uploadedAt: string;

  counts: IBieOnboardingImportCounts;
  issues: IBieOnboardingImportIssueCounts;
  resolution: IBieOnboardingImportResolution;

  validationStatus: BieOnboardingValidationStatus;
  validationReady: BieFlag;

  status: number;
  activeStatus: number;

  stamp: string;
}


/* ============================================================
 * Validation summary
 * ============================================================
 */

export interface IBieOnboardingImportClassSummary {
  imports: number;
  items: number;
}

export interface IBieValidationSummaryImports {
  total: number;

  classes: Record<
    string,
    IBieOnboardingImportClassSummary
  >;
}

export interface IBieValidationSummaryItems {
  total: number;
  pending: number;
  valid: number;
  warning: number;
  failed: number;
}

export interface IBieValidationSummaryIssues {
  total: number;
  information: number;
  warning: number;
  error: number;
  blocking: number;
}

export interface IBieValidationResolutionBreakdown {
  recordClass: number;
  classCompatibility: number;
  driver: number;
  product: number;
  location: number;
  entity: number;
  period: number;
  account: number;
  unit: number;
}

export interface IBieValidationResolutionSummary {
  resolvedRecordClass: number;
  unresolvedRecordClass: number;
  unresolvedReferences: number;

  breakdown: IBieValidationResolutionBreakdown;
}

export interface IBieValidationImportSummary {
  importId: BieRecordId;
  importIx: string;

  importCode: string;
  importName: string;

  classId: BieRecordId;
  classCode: BieOnboardingImportClassCode;
  className: string;

  sheetName: string;
  filename: string;

  total: number;
  pending: number;
  valid: number;
  warning: number;
  failed: number;

  issues: number;
  blockingIssues: number;
  unresolvedReferences: number;

  validationStatus: BieOnboardingValidationStatus;
  validationReady: BieFlag;
}

export interface IBieValidationStateSummary {
  stateId: BieRecordId;
  stateCode: BieValidationStateCode;
  stateName: string;

  sortOrder: number;
  count: number;
}

export interface IBieValidationSeveritySummary {
  severityId: BieRecordId;
  severityCode: BieValidationSeverityCode;
  severityName: string;

  severityLevel: number;
  isBlocking: BieFlag;

  count: number;
}

export interface IBieValidationRuleSummary {
  ruleId: BieRecordId;
  ruleCode: string;
  ruleName: string;

  severityCode: BieValidationSeverityCode;
  isBlocking: BieFlag;

  count: number;
}

export interface IBieOnboardingValidationSummary {
  loadSessionId: BieRecordId;
  loadSessionIx: string;

  loadSessionCode: string;
  loadSessionName: string;

  stateId: BieRecordId;
  stateCode: BieLoadSessionStateCode;
  stateName: string;

  scopeId: BieRecordId;
  scopeCode: string;
  scopeName: string;

  sourceOrganization: string;
  sourceSystem: string;

  coverageStartDate: string | null;
  coverageEndDate: string | null;
  historicalCutoffDate: string | null;

  observedCoverageStartDate: string | null;
  observedCoverageEndDate: string | null;

  validatedBy?: BieRecordId | null;
  validatedAt?: string | null;

  validationStatus: BieOnboardingValidationStatus;
  validationReady: BieFlag;

  imports: IBieValidationSummaryImports;
  items: IBieValidationSummaryItems;
  issues: IBieValidationSummaryIssues;
  resolution: IBieValidationResolutionSummary;

  byImport: IBieValidationImportSummary[];
  byValidationState: IBieValidationStateSummary[];
  bySeverity: IBieValidationSeveritySummary[];
  byRule: IBieValidationRuleSummary[];

  status: number;
  activeStatus: number;

  stamp: string;
}


/* ============================================================
 * Validation issue
 * ============================================================
 */

export interface IBieOnboardingValidationIssue {
  rid: BieRecordId;
  rix: string;

  iti: BieRecordId;
  itx: string;

  imi: BieRecordId;
  imx: string;
  imc: string;
  imn: string;

  lsi: BieRecordId;
  lsx: string;
  lsc: string;
  lsn: string;

  cli: BieRecordId;
  clx: string;
  clc: BieOnboardingImportClassCode;
  cln: string;

  lin: number;

  ssh: string;
  srw: number;
  srf: string;

  rli: BieRecordId;
  rlx: string;
  rlc: string;
  rln: string;

  exo: number;

  svi: BieRecordId;
  svx: string;
  svc: BieValidationSeverityCode;
  svn: string;

  svl: number;
  ibl: BieFlag;

  fld: string;
  src?: string | null;

  msg: string;

  vdd: Record<string, unknown>;

  ict:
  | 'BLOCKING'
  | 'REVIEW'
  | 'INFORMATION'
  | string;

  dcd: string;

  exi: Record<string, unknown>;

  sts: number;
  ast: number;

  stp: string;
}


/* ============================================================
 * Validation rule
 * ============================================================
 */

export interface IBieOnboardingValidationRule {
  rid: BieRecordId;
  rix: string;

  svi: BieRecordId;
  svx: string;
  svc: BieValidationSeverityCode;
  svn: string;

  svl: number;
  ibl: BieFlag;

  nam: string;
  shc: string;
  dsc?: string | null;

  exo: number;

  sts: number;
  ast: number;

  stp: string;
}


/* ============================================================
 * Workbench lookups
 * ============================================================
 */

export interface IBieOnboardingWorkbenchLookups {
  importClasses: IBieOnboardingLookup[];
  recordClasses: IBieOnboardingLookup[];

  validationStates:
  IBieValidationStateLookup[];

  severities:
  IBieValidationSeverityLookup[];

  rules:
  IBieValidationRuleLookup[];
}


/* ============================================================
 * Main Workbench contract
 * ============================================================
 */

export interface IBieOnboardingWorkbench {
  loadSession: IBieOnboardingWorkbenchLoadSession;

  actions: IBieOnboardingWorkbenchActions;

  summary: IBieOnboardingValidationSummary;

  imports: IBieOnboardingWorkbenchImport[];

  recentIssues: IBieOnboardingValidationIssue[];

  lookups: IBieOnboardingWorkbenchLookups;
}


/* ============================================================
 * Validation operation
 * ============================================================
 */

export interface IBieOnboardingResolutionCounts {
  recordClass: number;
  scenario: number;
  driver: number;
  product: number;
  entity: number;
  location: number;
  account: number;
  unit: number;
  period: number;
  classCompatibility: number;
}

export interface IBieOnboardingResolutionResult {
  loadSessionId: BieRecordId;
  loadSessionCode: string;
  loadSessionName: string;

  loadSessionState: BieLoadSessionStateCode;

  importCount: number;
  itemCount: number;

  itemsResetToPending: number;
  validationIssuesCleared: number;
  importsRefreshed: number;

  resolved: IBieOnboardingResolutionCounts;
  unresolved: IBieOnboardingResolutionCounts;

  validationStatus: BieValidationStateCode;
  resolutionStatus: string;

  resolvedBy: BieRecordId;
  resolvedAt: string;
}

export interface IBieOnboardingValidationResult {
  loadSessionId: BieRecordId;
  loadSessionCode: string;
  loadSessionName: string;

  importCount: number;
  itemCount: number;

  validItems: number;
  warningItems: number;
  failedItems: number;

  issueCount: number;
  infoIssueCount: number;
  warningIssueCount: number;
  errorIssueCount: number;
  blockingIssueCount: number;

  importsRefreshed: number;

  validationStatus: BieOnboardingValidationStatus;

  validatedBy: BieRecordId;
  validatedAt: string;
}

export interface IBieOnboardingWorkbookValidation {
  loadSessionId: BieRecordId;
  loadSessionCode: string;
  loadSessionName: string;

  previousState: BieLoadSessionStateCode;
  state: BieLoadSessionStateCode;

  resolution: IBieOnboardingResolutionResult;
  validation: IBieOnboardingValidationResult;

  validatedBy: BieRecordId;
  validatedAt: string;

  stamp: string;
}

export interface IBieOnboardingWorkbookValidationResult {
  loadSessionId: BieRecordId;

  previousState: BieLoadSessionStateCode;
  state: BieLoadSessionStateCode;

  resolution: IBieOnboardingResolutionResult;
  validation: IBieOnboardingValidationResult;

  validatedBy: BieRecordId;
  validatedAt: string;

  stamp: string;
}

export interface IBieOnboardingValidateAndRefreshResults {
  validation: IBieOnboardingWorkbookValidationResult;
  workbench: IBieOnboardingWorkbench;
}

export interface IBieOnboardingValidateAndRefreshResult {
  validation: IBieOnboardingWorkbookValidation;
  workbench: IBieOnboardingWorkbench;
}

export interface IBieOnboardingValidateRequest {
  stamp: string;
}


/* ============================================================
 * Issue search request
 * ============================================================
 */

export interface IBieValidationIssueFindRequest {
  loadSessionId?: BieRecordId | null;
  importId?: BieRecordId | null;
  importItemId?: BieRecordId | null;

  severityCode?: BieValidationSeverityCode | null;
  ruleCode?: string | null;
  classCode?: BieOnboardingImportClassCode | null;

  blocking?: BieFlag | boolean | null;

  search?: string | null;
  status?: number | null;

  pageOffset?: number;
  pageLimit?: number;
}


/* ============================================================
 * Rule search request
 * ============================================================
 */

export interface IBieValidationRuleFindRequest {
  severityCode?: BieValidationSeverityCode | null;
  blocking?: BieFlag | boolean | null;

  search?: string | null;
  status?: number | null;
}


/* ============================================================
 * Workbench component state
 * ============================================================
 */

export type BieOnboardingWorkbenchTab =
  | 'OVERVIEW'
  | 'STAGING'
  | 'IMPORTS'
  | 'ISSUES'
  | 'RULES';

export interface IBieOnboardingWorkbenchFilters {
  importId: BieRecordId | '';

  severityCode: string;
  ruleCode: string;
  classCode: string;

  blocking: '' | '0' | '1';

  search: string;
}

export interface IBieOnboardingWorkbenchState {
  loading: boolean;
  validating: boolean;
  refreshing: boolean;

  errorMessage: string;
  successMessage: string;

  selectedTab: BieOnboardingWorkbenchTab;

  workbench: IBieOnboardingWorkbench | null;

  issues: IBieOnboardingValidationIssue[];
  rules: IBieOnboardingValidationRule[];

  filters: IBieOnboardingWorkbenchFilters;

  issuePageOffset: number;
  issuePageLimit: number;
}





/* ============================================================
 * Default values
 * ============================================================
 */

export const emptyBieOnboardingWorkbenchFilters:
  IBieOnboardingWorkbenchFilters = {
  importId: '',

  severityCode: '',
  ruleCode: '',
  classCode: '',

  blocking: '',

  search: '',
};

export const emptyBieOnboardingWorkbenchState:
  IBieOnboardingWorkbenchState = {
  loading: false,
  validating: false,
  refreshing: false,

  errorMessage: '',
  successMessage: '',

  selectedTab: 'OVERVIEW',

  workbench: null,

  issues: [],
  rules: [],

  filters: {
    ...emptyBieOnboardingWorkbenchFilters,
  },

  issuePageOffset: 0,
  issuePageLimit: 100,
};





/* ============================================================
 * Utility functions
 * ============================================================
 */

export function isBieFlagEnabled(
  value: string | BieFlag | boolean | null | undefined,
): boolean {
  return value === true || Number(value) === 1;
}

export function getBieValidationStatusLabel(
  status: BieOnboardingValidationStatus,
): string {
  switch (
  String(status || '').toUpperCase()
  ) {
    case 'VALID':
      return 'Valid';

    case 'WARNING':
      return 'Warning';

    case 'FAILED':
      return 'Failed';

    case 'PENDING':
      return 'Pending Validation';

    case 'EMPTY':
      return 'No Imported Records';

    default:
      return status || 'Unknown';
  }
}

export function getBieLoadSessionStateLabel(
  stateCode: BieLoadSessionStateCode,
): string {
  switch (
  String(stateCode || '').toUpperCase()
  ) {
    case 'DRAFT':
      return 'Draft';

    case 'STAGED':
      return 'Staged';

    case 'VALIDATED':
      return 'Validated';

    case 'REJECTED':
      return 'Rejected';

    case 'PUBLISHED':
      return 'Published';

    case 'CANCELLED':
      return 'Cancelled';

    case 'ROLLEDBACK':
      return 'Rolled Back';

    default:
      return stateCode || 'Unknown';
  }
}

export function getBieImportClassLabel(
  classCode: BieOnboardingImportClassCode,
): string {
  switch (
  String(classCode || '').toUpperCase()
  ) {
    case 'PRODUCT':
      return 'Products';

    case 'LOCATION':
      return 'Locations';

    case 'HISTORICAL':
      return 'Historical Facts';

    case 'RECONCILIATION':
      return 'Reconciliation';

    default:
      return classCode || 'Unknown';
  }
}